import {
  Button,
  ErrorMessage,
  InputField,
  Spinner,
  SuccessPopup,
} from "@/common/components";
import { useContext, useEffect, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useSaveContractAndCertificateMutation } from "@/service/ProductService";
import { scrollToTop } from "@/utils/Utilities";
import { validate } from "@/utils/Validations";

const ContractCertificateDetails = () => {
  const {
    setNavHeader,
    location,
    debounceState,
    debounceErrors,
    cancel,
    labels,
    setPageHeader,
    cloneDeep,
    accessorPrivileges,
    isRiccAdministrator,
  } = useContext(PrivateContext);

  const [contractData, setContractData] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const [saveContractCertificateApi, { isLoading: isFetching, error }] =
    useSaveContractAndCertificateMutation();

  const readOnlyAccess = !(
    accessorPrivileges["PRODUCT"] === "ADD" ||
    accessorPrivileges["PRODUCT"] === "UPDATE" ||
    isRiccAdministrator
  );

  const onChange = async (value, state) => {
    debounceState({ ...contractData, [state]: value }, setContractData);
    debounceErrors(
      { ...errors, [state]: await validate(value, [state]) },
      setErrors
    );
  };

  const errorProps = (state) => {
    return {
      value: contractData[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const saveContractDetails = () => {
    scrollToTop();
    const payload = cloneDeep(contractData);

    if (contractData?.contractNumberExists) {
      payload.contractNumber = null;
    }

    saveContractCertificateApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    setPageHeader(labels?.contractCertificate?.pageHeader);
    setNavHeader(labels?.contractCertificate?.navHeader);

    if (location?.state?.planId) {
      setContractData(location.state);
    }
  }, []);

  return (
    <section className="container-medium py-5">
      {isFetching && <Spinner />}
      <ErrorMessage error={error} wrapperClassName="mb-3" />

      {contractData?.planId && (
        <>
          <div className="division">
            <span className="left">
              {labels?.contractCertificate?.planName}
            </span>
            <span className="right">{contractData?.planName}</span>
          </div>

          <div className="division">
            <span className="left">{labels?.contractCertificate?.planId}</span>
            <span className="right">{contractData?.planId}</span>
          </div>

          <div className="division">
            <span className="left">
              {labels?.contractCertificate?.recordKeeper}
            </span>
            <span className="right">{contractData?.recordkeeper}</span>
          </div>

          {(contractData?.contractNumberExists || readOnlyAccess) && (
            <div className="division">
              <span className="left">
                {labels?.contractCertificate?.contractNumberHeader}
              </span>
              <span className="right">{contractData?.contractNumber}</span>
            </div>
          )}

          {(contractData?.startingCertificateNumberExists ||
            readOnlyAccess) && (
            <div className="division">
              <span className="left">
                {labels?.contractCertificate?.certificateNumber}
              </span>
              <span className="right">
                {contractData?.startingCertificateNumber}
              </span>
            </div>
          )}

          <div className="division">
            <span className="left">
              {labels?.contractCertificate?.stateOfIssue}
            </span>
            <span className="right">{contractData?.stateOfIssue}</span>
          </div>

          <div className="division">
            <span className="left">
              {labels?.contractCertificate?.planType}
            </span>
            <span className="right">{contractData?.planType}</span>
          </div>

          <div className="division">
            <span className="left">{labels?.contractCertificate?.planEnd}</span>
            <span className="right">
              {contractData?.planYearEnd.substring(0, 2) +
                "/" +
                contractData?.planYearEnd.substring(2)}
            </span>
          </div>

          <div className="division">
            <span className="left">{labels?.contractCertificate?.lob}</span>
            <span className="right">{contractData?.planExternalLOBId}</span>
          </div>

          <div className="division">
            <span className="left">
              {labels?.contractCertificate?.deferral}
            </span>
            <span className="right">{contractData?.deferralPlanId}</span>
          </div>

          <div className="division mb-5">
            <span className="left">
              {labels?.contractCertificate?.deferralLOB}
            </span>
            <span className="right">{contractData?.deferralPlanICU}</span>
          </div>

          {!contractData?.contractNumberExists && !readOnlyAccess && (
            <InputField
              id="contractNumber"
              label={labels?.contractCertificate?.contractNumber}
              maxLength="20"
              change={(e) => onChange(e, "contractNumber")}
              {...errorProps("contractNumber")}
            >
              <small>{labels?.contractCertificate?.onceContractAdded}</small>
            </InputField>
          )}

          {!contractData?.startingCertificateNumberExists &&
            !readOnlyAccess && (
              <InputField
                id="certNumber"
                type="number"
                label={labels?.contractCertificate?.certNumber}
                change={(e) => onChange(e, "startingCertificateNumber")}
                {...errorProps("startingCertificateNumber")}
              >
                <small>{labels?.contractCertificate?.onceCertAdded}</small>
              </InputField>
            )}

          {!readOnlyAccess &&
            !(
              contractData?.contractNumberExists &&
              contractData?.startingCertificateNumberExists
            ) && (
              <Button
                id="submit"
                text={labels?.contractCertificate?.submit}
                wrapperClassName="col-md-4 ms-auto mt-5"
                disabled={
                  !contractData?.contractNumber ||
                  !contractData?.startingCertificateNumber
                }
                click={saveContractDetails}
              />
            )}

          <SuccessPopup
            showModal={showPopup}
            buttonText={labels?.contractCertificate?.continue}
            click={cancel}
            body={labels?.contractCertificate?.requestCompleted}
          />
        </>
      )}
    </section>
  );
};

export default ContractCertificateDetails;
